import React, { useCallback, useEffect, useMemo, useState, Fragment } from 'react';
import { Badge } from './SubscriptionAppBadge';
import { Button } from './SubscriptionAppButton';
import { Pagination } from './SubscriptionAppPagination';
import { TableFilter } from './TableFilter';
import type { Filter } from './TableFilter';
import { ColumnToggle } from './ColumnToggle';
import type { Column } from './ColumnToggle';
import { XIcon, ChevronDown, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { SearchBar } from './SearchBar';
import { useNavigate } from 'react-router-dom';
import { BulkActionsManager } from './BulkActionsManager';
import type { TagData } from './TagInput';
import { useAppData } from '../contexts/AppDataContext';
import { ExportButton } from './ExportButton';
import { SavedViewsDropdown, SavedView } from './SavedViewsDropdown';
interface SubscriptionTableProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  filterValues?: Record<string, string[]>;
  onFilterValuesChange?: (filterValues: Record<string, string[]>) => void;
  createButton?: React.ReactNode;
}
// Helper function to map status to Badge variant
const getStatusVariant = (status: string): 'success' | 'warning' | 'info' | 'error' | 'default' => {
  if (status === 'Active') return 'success';
  if (status === 'At Risk') return 'error';
  if (status === 'On Hold') return 'warning';
  if (status === 'Paused') return 'info';
  if (status === 'Cancelled') return 'default';
  return 'default';
};
// Helper function to format end date relative to now
const formatEndDate = (endDate?: string): string => {
  if (!endDate) return '—';
  const now = new Date();
  const end = new Date(endDate);
  const diffMs = now.getTime() - end.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffHours < 24) {
    return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  }
  if (diffDays <= 28) {
    return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
  }
  return end.toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};
// Helper function to calculate last order date
const calculateLastOrderDate = (nextPayment: string, subscriptionTitle: string): string => {
  const next = new Date(nextPayment);
  if (subscriptionTitle.includes('Every Month')) {
    const lastOrder = new Date(next);
    lastOrder.setMonth(lastOrder.getMonth() - 1);
    return lastOrder.toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } else if (subscriptionTitle.includes('Every 3 Months')) {
    const lastOrder = new Date(next);
    lastOrder.setMonth(lastOrder.getMonth() - 3);
    return lastOrder.toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } else if (subscriptionTitle.includes('Every 6 Months')) {
    const lastOrder = new Date(next);
    lastOrder.setMonth(lastOrder.getMonth() - 6);
    return lastOrder.toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }
  return '—';
};
// Helper function to format next retry time
const formatNextRetry = (nextRetry?: string): {
  text: string;
  isOverdue: boolean;
} => {
  if (!nextRetry) return {
    text: '—',
    isOverdue: false
  };
  const retryDate = new Date(nextRetry);
  const now = new Date();
  const diffMs = retryDate.getTime() - now.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffMs < 0) {
    return {
      text: '—',
      isOverdue: false
    };
  }
  if (diffHours < 48) {
    if (diffHours < 1) {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return {
        text: `In ${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'}`,
        isOverdue: false
      };
    }
    if (diffHours < 24) {
      return {
        text: `In ${diffHours} ${diffHours === 1 ? 'hour' : 'hours'}`,
        isOverdue: false
      };
    }
    return {
      text: `Tomorrow ${retryDate.toLocaleTimeString('en-AU', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })}`,
      isOverdue: false
    };
  }
  return {
    text: retryDate.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short'
    }) + ', ' + retryDate.toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }),
    isOverdue: false
  };
};
// Helper to get payment status sort value
const getPaymentStatusSortValue = (status?: string): number => {
  if (!status || status === 'healthy') return 0;
  if (status === 'in-retry') return 1;
  if (status === 'on-hold') return 2;
  if (status === 'failed') return 3;
  return 0;
};
export function SubscriptionTable({
  searchQuery,
  onSearchChange,
  filterValues: externalFilterValues,
  onFilterValuesChange,
  createButton
}: SubscriptionTableProps) {
  const navigate = useNavigate();
  const {
    subscriptions,
    allTags,
    getSubscriptionTags
  } = useAppData();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({
    key: 'nextOrderDate',
    direction: 'asc'
  });
  const [customDateRanges, setCustomDateRanges] = useState<Record<string, {
    startDate: string;
    endDate: string;
  }>>({});
  const [columnVisibility, setColumnVisibility] = useState<Column[]>([{
    id: 'status',
    label: 'Status',
    visible: true
  }, {
    id: 'paymentStatus',
    label: 'Payment Status',
    visible: true
  }, {
    id: 'nextRetry',
    label: 'Next Retry',
    visible: true
  }, {
    id: 'id',
    label: 'ID',
    visible: true
  }, {
    id: 'customer',
    label: 'Customer',
    visible: true
  }, {
    id: 'amount',
    label: 'Amount',
    visible: true
  }, {
    id: 'details',
    label: 'Details',
    visible: true
  }, {
    id: 'orders',
    label: 'Orders',
    visible: false
  }, {
    id: 'tags',
    label: 'Tags',
    visible: true
  }, {
    id: 'nextOrderDate',
    label: 'Next Order Date',
    visible: true
  }, {
    id: 'lastOrderDate',
    label: 'Last Order Date',
    visible: false
  }, {
    id: 'startDate',
    label: 'Start Date',
    visible: false
  }, {
    id: 'endDate',
    label: 'End Date',
    visible: false
  }, {
    id: 'items',
    label: 'Items',
    visible: true
  }]);
  const [internalFilterValues, setInternalFilterValues] = useState<Record<string, string[]>>({
    status: [],
    paymentStatus: [],
    expiringCards: [],
    tags: [],
    id: [],
    customer: [],
    amount: [],
    subscriptionTitle: [],
    nextOrderDate: [],
    startDate: []
  });
  // Saved Views State
  const [currentView, setCurrentView] = useState('All Subscriptions');
  const [savedViews, setSavedViews] = useState<SavedView[]>([{
    id: 'default',
    name: 'All Subscriptions',
    type: 'default'
  }, {
    id: 'vip-failed',
    name: 'VIP Failed Payments',
    type: 'mine'
  }, {
    id: 'paused-30',
    name: 'Paused Over 30 Days',
    type: 'mine'
  }, {
    id: 'at-risk',
    name: 'At Risk Members',
    type: 'mine'
  }]);
  const handleViewSelect = (view: SavedView) => {
    setCurrentView(view.name);
    // Mock applying filters based on view
    // In a real app, these would be stored in the view object
    const newFilters = {
      status: [],
      paymentStatus: [],
      expiringCards: [],
      tags: [],
      id: [],
      customer: [],
      amount: [],
      subscriptionTitle: [],
      nextOrderDate: [],
      startDate: []
    };
    if (view.id === 'vip-failed') {
      // Tags contains VIP + Payment Status is Failed
      // Need to find VIP tag ID from allTags
      const vipTag = allTags.find(t => t.label === 'VIP');
      if (vipTag) newFilters.tags = [vipTag.id];
      newFilters.paymentStatus = ['failed'];
    } else if (view.id === 'paused-30') {
      // Status is Paused
      newFilters.status = ['paused'];
    } else if (view.id === 'at-risk') {
      // Status is At Risk
      newFilters.status = ['at-risk'];
    } else if (view.id === 'high-value') {
      // Amount > $200 + Details contains "3 Months"
      newFilters.amount = ['200-500', 'over-500'];
      newFilters.subscriptionTitle = ['every-3-months'];
    }
    // Apply filters
    if (onFilterValuesChange) {
      onFilterValuesChange(newFilters);
    } else {
      setInternalFilterValues(newFilters);
    }
    // Clear search when switching views
    onSearchChange('');
  };
  const handleSaveView = (name: string, isShared: boolean) => {
    const newView: SavedView = {
      id: Date.now().toString(),
      name,
      type: isShared ? 'team' : 'mine',
      creator: isShared ? 'You' : undefined,
      isShared
    };
    setSavedViews([...savedViews, newView]);
    setCurrentView(name);
  };
  const handleDeleteView = (viewId: string) => {
    setSavedViews(savedViews.filter(v => v.id !== viewId));
    if (currentView === savedViews.find(v => v.id === viewId)?.name) {
      setCurrentView('All Subscriptions');
      handleClearAll();
    }
  };
  const handleDuplicateView = (viewId: string) => {
    const viewToDuplicate = savedViews.find(v => v.id === viewId);
    if (viewToDuplicate) {
      const newView: SavedView = {
        id: Date.now().toString(),
        name: `Copy of ${viewToDuplicate.name}`,
        type: 'mine',
        creator: undefined,
        isShared: false
      };
      setSavedViews([...savedViews, newView]);
    }
  };
  const handleRenameView = (viewId: string, newName: string) => {
    setSavedViews(savedViews.map(v => v.id === viewId ? {
      ...v,
      name: newName
    } : v));
    // Update currentView if the renamed view was the active one
    const renamedView = savedViews.find(v => v.id === viewId);
    if (renamedView && currentView === renamedView.name) {
      setCurrentView(newName);
    }
  };
  const filterValues = externalFilterValues || internalFilterValues;
  const setFilterValues = onFilterValuesChange || setInternalFilterValues;
  const [openFilterDropdowns, setOpenFilterDropdowns] = useState<Set<string>>(new Set());
  const [addedFilters, setAddedFilters] = useState<Set<string>>(new Set());
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  useEffect(() => {
    const filtersWithValues = Object.keys(filterValues).filter(key => filterValues[key] && filterValues[key].length > 0);
    if (filtersWithValues.length > 0) {
      setAddedFilters(prev => {
        const newSet = new Set(prev);
        filtersWithValues.forEach(filterId => newSet.add(filterId));
        return newSet;
      });
    }
  }, [filterValues]);
  const tableFilters: Filter[] = [{
    id: 'status',
    label: 'Status',
    options: [{
      id: '1',
      label: 'Active',
      value: 'active'
    }, {
      id: '2',
      label: 'At Risk',
      value: 'at-risk'
    }, {
      id: '3',
      label: 'On Hold',
      value: 'on-hold'
    }, {
      id: '4',
      label: 'Paused',
      value: 'paused'
    }, {
      id: '5',
      label: 'Cancelled',
      value: 'cancelled'
    }],
    selectedValues: filterValues.status || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('status'),
    filterFn: (item, selectedValues) => {
      return selectedValues.some(val => item.status.toLowerCase() === val.toLowerCase().replace('-', ' '));
    }
  }, {
    id: 'paymentStatus',
    label: 'Payment Status',
    options: [{
      id: '2',
      label: 'In Retry',
      value: 'in-retry'
    }, {
      id: '4',
      label: 'Failed',
      value: 'failed'
    }],
    selectedValues: filterValues.paymentStatus || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('paymentStatus'),
    filterFn: (item, selectedValues) => {
      const status = item.paymentInfo?.status || 'healthy';
      return selectedValues.some(val => status === val);
    }
  }, {
    id: 'expiringCards',
    label: 'Expiring Cards',
    options: [{
      id: '1',
      label: 'Expiring Soon (60 days)',
      value: 'expiring-soon'
    }],
    selectedValues: filterValues.expiringCards || [],
    multiSelect: false,
    isOpen: openFilterDropdowns.has('expiringCards'),
    filterFn: (item, selectedValues) => {
      if (selectedValues.length === 0) return true;
      if (!item.cardExpiry) return false;
      // Calculate days until expiry
      const expiryDate = new Date(item.cardExpiry.expiryYear, item.cardExpiry.expiryMonth, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diffMs = expiryDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      return diffDays >= 0 && diffDays <= 60;
    }
  }, {
    id: 'tags',
    label: 'Tags',
    options: allTags.map(tag => ({
      id: tag.id,
      label: tag.label,
      value: tag.id
    })),
    selectedValues: filterValues.tags || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('tags'),
    filterFn: (item, selectedValues) => {
      const itemTags = getSubscriptionTags(item.id);
      return selectedValues.some(val => itemTags?.some(tag => tag.id === val));
    }
  }, {
    id: 'id',
    label: 'ID',
    options: [],
    selectedValues: filterValues.id || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('id'),
    searchable: true,
    filterFn: (item, selectedValues) => {
      if (selectedValues.length === 0) return true;
      return selectedValues.some(val => item.id.toLowerCase().includes(val.toLowerCase()));
    }
  }, {
    id: 'customer',
    label: 'Customer',
    options: [],
    selectedValues: filterValues.customer || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('customer'),
    searchable: true,
    filterFn: (item, selectedValues) => {
      if (selectedValues.length === 0) return true;
      return selectedValues.some(val => item.customerName.toLowerCase().includes(val.toLowerCase()) || item.customer.toLowerCase().includes(val.toLowerCase()));
    }
  }, {
    id: 'amount',
    label: 'Amount',
    options: [{
      id: '1',
      label: 'Under $50',
      value: 'under-50'
    }, {
      id: '2',
      label: '$50 - $100',
      value: '50-100'
    }, {
      id: '3',
      label: '$100 - $200',
      value: '100-200'
    }, {
      id: '4',
      label: '$200 - $500',
      value: '200-500'
    }, {
      id: '5',
      label: 'Over $500',
      value: 'over-500'
    }],
    selectedValues: filterValues.amount || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('amount'),
    filterFn: (item, selectedValues) => {
      if (selectedValues.length === 0) return true;
      const amount = parseFloat(item.amount.replace(/[$,]/g, ''));
      return selectedValues.some(val => {
        if (val === 'under-50') return amount < 50;
        if (val === '50-100') return amount >= 50 && amount < 100;
        if (val === '100-200') return amount >= 100 && amount < 200;
        if (val === '200-500') return amount >= 200 && amount < 500;
        if (val === 'over-500') return amount >= 500;
        return false;
      });
    }
  }, {
    id: 'subscriptionTitle',
    label: 'Subscription Type',
    options: [{
      id: '1',
      label: 'Every Month',
      value: 'every-month'
    }, {
      id: '2',
      label: 'Every 3 Months',
      value: 'every-3-months'
    }, {
      id: '3',
      label: 'Every 6 Months',
      value: 'every-6-months'
    }],
    selectedValues: filterValues.subscriptionTitle || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('subscriptionTitle'),
    filterFn: (item, selectedValues) => {
      if (selectedValues.length === 0) return true;
      return selectedValues.some(val => {
        if (val === 'every-month') return item.subscriptionTitle.includes('Every Month');
        if (val === 'every-3-months') return item.subscriptionTitle.includes('Every 3 Months');
        if (val === 'every-6-months') return item.subscriptionTitle.includes('Every 6 Months');
        return false;
      });
    }
  }, {
    id: 'nextOrderDate',
    label: 'Next Order Date',
    options: [{
      id: '1',
      label: 'Next 7 days',
      value: 'next-7-days'
    }, {
      id: '2',
      label: 'Next 30 days',
      value: 'next-30-days'
    }, {
      id: '3',
      label: 'Next 3 months',
      value: 'next-3-months'
    }],
    selectedValues: filterValues.nextOrderDate || [],
    multiSelect: false,
    isOpen: openFilterDropdowns.has('nextOrderDate'),
    filterFn: (item, selectedValues) => {
      if (selectedValues.length === 0) return true;
      const now = new Date();
      const nextPayment = new Date(item.nextPayment);
      const diffMs = nextPayment.getTime() - now.getTime();
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      return selectedValues.some(val => {
        if (val === 'next-7-days') return diffDays >= 0 && diffDays <= 7;
        if (val === 'next-30-days') return diffDays >= 0 && diffDays <= 30;
        if (val === 'next-3-months') return diffDays >= 0 && diffDays <= 90;
        return false;
      });
    }
  }, {
    id: 'startDate',
    label: 'Start Date',
    options: [{
      id: '1',
      label: 'Last 7 days',
      value: 'last-7-days'
    }, {
      id: '2',
      label: 'Last 30 days',
      value: 'last-30-days'
    }, {
      id: '3',
      label: 'Last 3 months',
      value: 'last-3-months'
    }, {
      id: '4',
      label: 'Last 6 months',
      value: 'last-6-months'
    }, {
      id: '5',
      label: 'Last year',
      value: 'last-year'
    }],
    selectedValues: filterValues.startDate || [],
    multiSelect: false,
    isOpen: openFilterDropdowns.has('startDate'),
    filterFn: (item, selectedValues) => {
      if (selectedValues.length === 0) return true;
      if (!item.startDate) return false;
      const now = new Date();
      const startDate = new Date(item.startDate);
      const diffMs = now.getTime() - startDate.getTime();
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      return selectedValues.some(val => {
        if (val === 'last-7-days') return diffDays >= 0 && diffDays <= 7;
        if (val === 'last-30-days') return diffDays >= 0 && diffDays <= 30;
        if (val === 'last-3-months') return diffDays >= 0 && diffDays <= 90;
        if (val === 'last-6-months') return diffDays >= 0 && diffDays <= 180;
        if (val === 'last-year') return diffDays >= 0 && diffDays <= 365;
        return false;
      });
    }
  }];
  const hasActiveFilters = addedFilters.size > 0 || searchQuery.length > 0;
  const handleAddFilter = (filterId: string) => {
    const filter = tableFilters.find(f => f.id === filterId);
    if (filter) {
      setAddedFilters(prev => new Set(prev).add(filterId));
      if (filter.multiSelect && filter.options.length > 0) {
        handleFilterChange(filterId, [filter.options[0].value]);
        setTimeout(() => handleToggleFilterDropdown(filterId), 0);
      }
    }
  };
  const filteredAndSortedSubscriptions = useMemo(() => {
    let filtered = subscriptions;
    tableFilters.forEach(filter => {
      const selectedValues = filter.selectedValues;
      if (selectedValues && selectedValues.length > 0 && filter.filterFn) {
        filtered = filtered.filter(item => filter.filterFn!(item, selectedValues, customDateRanges));
      }
    });
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item => item.customerName.toLowerCase().includes(query) || item.customer.toLowerCase().includes(query) || item.id.toLowerCase().includes(query));
    }
    // Auto-sort by card expiry when expiring cards filter is active
    const isExpiringCardsFilterActive = filterValues.expiringCards && filterValues.expiringCards.length > 0;
    if (isExpiringCardsFilterActive) {
      // Sort by soonest expiry first
      filtered = [...filtered].sort((a, b) => {
        if (!a.cardExpiry || !b.cardExpiry) return 0;
        const aExpiry = new Date(a.cardExpiry.expiryYear, a.cardExpiry.expiryMonth, 0).getTime();
        const bExpiry = new Date(b.cardExpiry.expiryYear, b.cardExpiry.expiryMonth, 0).getTime();
        return aExpiry - bExpiry;
      });
    } else if (sortConfig) {
      // Apply normal sorting
      const sorted = [...filtered].sort((a, b) => {
        let aValue: any;
        let bValue: any;
        switch (sortConfig.key) {
          case 'status':
            aValue = a.status;
            bValue = b.status;
            break;
          case 'paymentStatus':
            aValue = getPaymentStatusSortValue(a.paymentInfo?.status);
            bValue = getPaymentStatusSortValue(b.paymentInfo?.status);
            break;
          case 'nextRetry':
            const aRetry = a.paymentInfo?.nextRetry;
            const bRetry = b.paymentInfo?.nextRetry;
            if (!aRetry && !bRetry) return 0;
            if (!aRetry) return 1;
            if (!bRetry) return -1;
            const aDate = new Date(aRetry).getTime();
            const bDate = new Date(bRetry).getTime();
            const now = Date.now();
            if (aDate < now && bDate < now) {
              aValue = aDate;
              bValue = bDate;
            } else if (aDate < now) {
              return sortConfig.direction === 'asc' ? -1 : 1;
            } else if (bDate < now) {
              return sortConfig.direction === 'asc' ? 1 : -1;
            } else {
              aValue = aDate;
              bValue = bDate;
            }
            break;
          case 'customer':
            aValue = a.customerName;
            bValue = b.customerName;
            break;
          case 'amount':
            aValue = parseFloat(a.amount.replace(/[$,]/g, ''));
            bValue = parseFloat(b.amount.replace(/[$,]/g, ''));
            break;
          case 'nextOrderDate':
            aValue = new Date(a.nextPayment).getTime();
            bValue = new Date(b.nextPayment).getTime();
            break;
          default:
            return 0;
        }
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
      return sorted;
    }
    return filtered;
  }, [subscriptions, filterValues, sortConfig, searchQuery, customDateRanges]);
  const visibleColumns = useMemo(() => {
    return columnVisibility.filter(col => col.visible);
  }, [columnVisibility]);
  const totalPages = Math.ceil(filteredAndSortedSubscriptions.length / pageSize);
  const paginatedSubscriptions = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredAndSortedSubscriptions.slice(startIndex, startIndex + pageSize);
  }, [currentPage, pageSize, filteredAndSortedSubscriptions]);
  const handleFilterChange = (filterId: string, value: string | string[]) => {
    setFilterValues({
      ...filterValues,
      [filterId]: Array.isArray(value) ? value : [value]
    });
  };
  const handleFilterRemove = (filterId: string) => {
    setFilterValues({
      ...filterValues,
      [filterId]: []
    });
    setAddedFilters(prev => {
      const newSet = new Set(prev);
      newSet.delete(filterId);
      return newSet;
    });
  };
  const handleToggleFilterDropdown = (filterId: string) => {
    setOpenFilterDropdowns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(filterId)) {
        newSet.delete(filterId);
      } else {
        newSet.add(filterId);
      }
      return newSet;
    });
  };
  const handleClearAll = () => {
    setFilterValues({
      status: [],
      paymentStatus: [],
      expiringCards: [],
      tags: [],
      id: [],
      customer: [],
      amount: [],
      subscriptionTitle: [],
      nextOrderDate: [],
      startDate: []
    });
    setAddedFilters(new Set());
    onSearchChange('');
  };
  const handleSort = (columnKey: string) => {
    setSortConfig(current => {
      if (!current || current.key !== columnKey) {
        return {
          key: columnKey,
          direction: 'asc'
        };
      }
      if (current.direction === 'asc') {
        return {
          key: columnKey,
          direction: 'desc'
        };
      }
      return null;
    });
  };
  const handleToggleRow = (rowId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };
  const handleSelectRow = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]);
  };
  const handleSelectAllPage = () => {
    const pageIds = paginatedSubscriptions.map(row => row.id);
    const allSelected = pageIds.every(id => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
    } else {
      setSelectedIds(prev => {
        const newIds = new Set([...prev, ...pageIds]);
        return Array.from(newIds);
      });
    }
  };
  const renderCell = (row: any, columnId: string) => {
    switch (columnId) {
      case 'status':
        return <button onClick={e => {
          e.stopPropagation();
          navigate(`/subscriptions/${row.id}`);
        }} className="cursor-pointer hover:opacity-80 transition-opacity" data-id="element-3421">
            <Badge variant={getStatusVariant(row.status)} data-id="element-3422">{row.status}</Badge>
          </button>;
      case 'paymentStatus':
        const paymentInfo = row.paymentInfo;
        if (!paymentInfo || paymentInfo.status === 'healthy') {
          return <span className="text-[13px] text-[#94A3B8]" data-id="element-3423">—</span>;
        }
        if (paymentInfo.status === 'in-retry') {
          const isFinalRetry = paymentInfo.retryCount === paymentInfo.maxRetries;
          return <div className="group relative" data-id="element-3424">
              <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${isFinalRetry ? 'bg-[#F59E0B] text-white border-[#F59E0B]' : 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]'}`} data-id="element-3425">
                {isFinalRetry ? 'Final Retry' : `Retry ${paymentInfo.retryCount}/${paymentInfo.maxRetries}`}
              </span>
              <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-50" data-id="element-3426">
                <div className="bg-[#1E293B] text-white text-xs rounded-md px-3 py-2 whitespace-nowrap shadow-lg" data-id="element-3427">
                  <div className="font-medium" data-id="element-3428">
                    Retry {paymentInfo.retryCount} of {paymentInfo.maxRetries}
                  </div>
                  {paymentInfo.nextRetry && <div className="text-[#94A3B8] mt-1" data-id="element-3429">
                      Next: {formatNextRetry(paymentInfo.nextRetry).text}
                    </div>}
                  {paymentInfo.failureReason && <div className="text-[#94A3B8] mt-1" data-id="element-3430">
                      Reason: {paymentInfo.failureReason}
                    </div>}
                </div>
              </div>
            </div>;
        }
        if (paymentInfo.status === 'on-hold') {
          return <span className="text-[13px] text-[#94A3B8]" data-id="element-3431">—</span>;
        }
        if (paymentInfo.status === 'failed') {
          return <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-[#EF4444] text-white border border-[#EF4444]" data-id="element-3432">
              Failed
            </span>;
        }
        return <span className="text-[13px] text-[#94A3B8]" data-id="element-3433">—</span>;
      case 'nextRetry':
        const retryInfo = row.paymentInfo;
        if (!retryInfo?.nextRetry) {
          return <span className="text-[13px] text-[#94A3B8]" data-id="element-3434">—</span>;
        }
        const {
          text
        } = formatNextRetry(retryInfo.nextRetry);
        return <span className="text-sm text-[#0F172A]" data-id="element-3435">{text}</span>;
      case 'id':
        return <button className="text-sm text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors cursor-pointer" onClick={e => {
          e.stopPropagation();
          navigate(`/subscriptions/${row.id}`);
        }} data-id="element-3436">
            {row.id}
          </button>;
      case 'customer':
        return <div data-id="element-3437">
            <div className="text-sm text-[#0F172A] font-medium" data-id="element-3438">
              {row.customerName}
            </div>
            <div className="text-[13px] text-[#94A3B8] mt-1" data-id="element-3439">
              {row.customer}
            </div>
          </div>;
      case 'amount':
        return <div data-id="element-3440">
            <div className="text-sm font-semibold text-[#0F172A]" data-id="element-3441">
              {row.amount}
            </div>
            <div className="text-[13px] text-[#94A3B8] mt-1" data-id="element-3442">
              {row.discountPercent}% discount
            </div>
          </div>;
      case 'details':
        return <div className="text-sm text-[#0F172A]" data-id="element-3443">{row.subscriptionTitle}</div>;
      case 'orders':
        return <button className="text-sm text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors cursor-pointer" onClick={e => {
          e.stopPropagation();
        }} data-id="element-3444">
            {row.orderCount}
          </button>;
      case 'items':
        const isExpanded = expandedRows.has(row.id);
        const bottleCount = parseInt(row.subscriptionTitle.match(/(\d+)\s+Bottle/)?.[1] || '0');
        return <button onClick={e => {
          e.stopPropagation();
          handleToggleRow(row.id);
        }} className="flex items-center gap-2 text-[#0EA5E9] hover:text-[#0284C7] transition-colors cursor-pointer" data-id="element-3445">
            {isExpanded ? <ChevronDown className="w-4 h-4" data-id="element-3446" /> : <ChevronRight className="w-4 h-4" data-id="element-3447" />}
            <span className="text-sm font-medium border-b border-[#0EA5E9] border-dashed" data-id="element-3448">
              {bottleCount} {bottleCount === 1 ? 'item' : 'items'}
            </span>
          </button>;
      case 'tags':
        const rowTags = getSubscriptionTags(row.id);
        const isOnHold = row.status === 'On Hold';
        const hasTags = rowTags && rowTags.length > 0;
        if (!hasTags && !isOnHold) {
          return <span className="text-[13px] text-[#94A3B8]" data-id="element-3449">—</span>;
        }
        return <div className="flex flex-wrap gap-1" data-id="element-3450">
            {hasTags && rowTags.map(tag => {
            const colorClasses = {
              blue: 'bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]',
              green: 'bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]',
              orange: 'bg-[#FED7AA] text-[#C2410C] border-[#FDBA74]',
              purple: 'bg-[#E9D5FF] text-[#6B21A8] border-[#D8B4FE]',
              gray: 'bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]'
            };
            return <span key={tag.id} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${colorClasses[tag.color]}`} data-id="element-3451">
                    {tag.label}
                  </span>;
          })}
            {isOnHold && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]" data-id="element-3452">
                Addressed
              </span>}
          </div>;
      case 'nextOrderDate':
        return <div data-id="element-3453">
            {row.status === 'Active' && row.isSkipped ? <div className="text-sm text-[#0F172A]" data-id="element-3454">
                {row.skipCount && row.skipCount > 1 ? `⏭️ (${row.skipCount}) → Next: ${row.nextPayment}` : `⏭️ → Next: ${row.nextPayment}`}
              </div> : <div className="text-sm text-[#0F172A]" data-id="element-3455">
                Upcoming: {row.nextPayment}
              </div>}
          </div>;
      default:
        return null;
    }
  };
  return <div className="space-y-6" data-id="element-3456">
      <div className="space-y-4" data-id="element-3457">
        <div className="bg-white p-6 rounded-lg border border-[#E2E8F0]" data-id="element-3458">
          <div className="flex flex-col gap-4" data-id="element-3459">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-id="element-3460">
              <div className="flex items-center gap-3 flex-1" data-id="element-3461">
                {createButton && <div className="flex-shrink-0" data-id="element-3462">{createButton}</div>}
                <div className="w-full lg:w-72 flex-shrink-0" data-id="element-3463">
                  <SearchBar placeholder="Search subscriptions..." value={searchQuery} onChange={onSearchChange} className="w-full" data-id="element-3464" />
                </div>

                {/* Saved Views Dropdown */}
                <SavedViewsDropdown currentView={currentView} onViewSelect={handleViewSelect} onSaveView={handleSaveView} onDeleteView={handleDeleteView} onDuplicateView={handleDuplicateView} onRenameView={handleRenameView} hasActiveFilters={addedFilters.size > 0} savedViews={savedViews} className="flex-shrink-0" data-id="element-3465" />
              </div>
              <div className="flex items-center gap-3 flex-shrink-0 ml-auto" data-id="element-3466">
                <ExportButton onExport={async type => console.log(`Exporting ${type}`)} data-id="element-3467" />
                <TableFilter filters={tableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyButton={true} data-id="element-3468" />
                <ColumnToggle columns={columnVisibility.filter(col => col.id !== 'id')} onChange={updatedColumns => {
                const idColumn = columnVisibility.find(col => col.id === 'id');
                if (idColumn) {
                  setColumnVisibility([idColumn, ...updatedColumns]);
                } else {
                  setColumnVisibility(updatedColumns);
                }
              }} data-id="element-3469" />
              </div>
            </div>
            {addedFilters.size > 0 && <div className="flex items-center gap-3" data-id="element-3470">
                <TableFilter filters={tableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyChips={true} data-id="element-3471" />
                <button onClick={handleClearAll} className="text-[13px] text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors" data-id="element-3472">
                  Clear all
                </button>
              </div>}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden" data-id="element-3473">
        {selectedIds.length > 0 && <div className="bg-white border-b border-[#E2E8F0] px-6 py-4" data-id="element-3474">
            <BulkActionsManager selectedIds={selectedIds} onClearSelection={() => setSelectedIds([])} onActionComplete={() => setSelectedIds([])} data-id="element-3475" />
          </div>}
        <div className="overflow-x-auto" data-id="element-3476">
          <table className="w-full" data-id="element-3477">
            <thead className="bg-[#F1F5F9]" data-id="element-3478">
              <tr data-id="element-3479">
                <th className="w-12 px-4 py-3 border-b border-[#E2E8F0]" data-id="element-3480">
                  <div className="flex items-center justify-center" data-id="element-3481">
                    <input type="checkbox" className="w-[18px] h-[18px] rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9] cursor-pointer" checked={paginatedSubscriptions.length > 0 && paginatedSubscriptions.every(row => selectedIds.includes(row.id))} onChange={handleSelectAllPage} data-id="element-3482" />
                  </div>
                </th>
                {visibleColumns.map(column => <th key={column.id} className="px-4 py-3 text-left text-xs font-semibold text-[#475569] uppercase tracking-wider" data-id="element-3483">
                    <button onClick={() => handleSort(column.id)} className="flex items-center gap-2 hover:text-[#0EA5E9] transition-colors" data-id="element-3484">
                      {column.label}
                      {sortConfig?.key === column.id ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-3485" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-3486" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-3487" />}
                    </button>
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]" data-id="element-3488">
              {paginatedSubscriptions.map(row => {
              const isExpanded = expandedRows.has(row.id);
              const isSelected = selectedIds.includes(row.id);
              return <Fragment key={row.id} data-id="element-3489">
                    <tr className={`transition-colors ${isSelected ? 'bg-[#F0F9FF]' : 'hover:bg-[#F8FAFC]'}`} data-id="element-3490">
                      <td className="px-4 py-4" data-id="element-3491">
                        <div className="flex items-center justify-center" data-id="element-3492">
                          <input type="checkbox" className="w-[18px] h-[18px] rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9] cursor-pointer" checked={isSelected} onChange={e => {
                        e.stopPropagation();
                        handleSelectRow(row.id);
                      }} data-id="element-3493" />
                        </div>
                      </td>
                      {visibleColumns.map(column => <td key={column.id} className="px-[15px] py-[15px] text-sm" data-id="element-3494">
                          {renderCell(row, column.id)}
                        </td>)}
                    </tr>
                    {isExpanded && <tr data-id="element-3495">
                        <td colSpan={visibleColumns.length + 1} className="px-4 py-4 bg-[#F8FAFC]" data-id="element-3496">
                          <div className="pl-14" data-id="element-3497">
                            <h4 className="text-[13px] font-semibold text-[#0F172A] mb-3" data-id="element-3498">
                              Items
                            </h4>
                            <div className="space-y-2" data-id="element-3499">
                              {row.items?.map((item: any, index: number) => <div key={index} className="text-sm text-[#475569] leading-relaxed" data-id="element-3500">
                                  <span className="font-medium text-[#0EA5E9]" data-id="element-3501">
                                    {item.quantity}
                                  </span>{' '}
                                  <span data-id="element-3502">{item.name}</span>
                                </div>)}
                            </div>
                          </div>
                        </td>
                      </tr>}
                  </Fragment>;
            })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-3503">
        <div className="text-[13px] text-[#64748B]" data-id="element-3504">
          Showing {(currentPage - 1) * pageSize + 1}-
          {Math.min(currentPage * pageSize, filteredAndSortedSubscriptions.length)}{' '}
          of {filteredAndSortedSubscriptions.length} subscriptions
        </div>
        <div className="flex items-center gap-2" data-id="element-3505">
          <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="px-4 py-2 border border-[#CBD5E1] rounded-md text-[13px] font-medium text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 ease-in-out" data-id="element-3506">
            Previous
          </button>
          <div className="flex items-center gap-1" data-id="element-3507">
            {Array.from({
            length: totalPages
          }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`w-10 h-10 rounded-md text-[13px] font-medium transition-colors duration-200 ease-in-out ${page === currentPage ? 'bg-[#0EA5E9] text-white' : 'text-[#475569] hover:bg-[#F8FAFC]'}`} data-id="element-3508">
                {page}
              </button>)}
          </div>
          <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="px-4 py-2 border border-[#CBD5E1] rounded-md text-[13px] font-medium text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 ease-in-out" data-id="element-3509">
            Next
          </button>
        </div>
      </div>
    </div>;
}